import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Container,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../providers/SnackBarProvider';
import { handleError, validateEmail, validatePassword, validateNonEmpty, verifyCaptchaToken } from '../utils/lib';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

const LoginCreateAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const showSnackbar = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const recaptcha = useRef();

  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    name: useRef(null),
    email: useRef(null),
    password: useRef(null),
    passwordRepeat: useRef(null),
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  
  useEffect(() => {
    refs.name.current.focus();
  }, []);

  const handleSubmit = async(e) => {
    e.preventDefault();

    if (!validateNonEmpty('name', name, setErrors, refs.name) || 
        !validateEmail('email', email, setErrors, refs.email) || 
        !validatePassword('password', password, setErrors, refs.password) || 
        !validatePasswordRepeat())
      return;

    const captchaValue = recaptcha.current.getValue()
    if (!captchaValue) {
      showSnackbar(t('login.captchaRequired'));
      return;
    }

    const validCaptcha = verifyCaptchaToken(captchaValue);
    if (!validCaptcha) {
      showSnackbar(t('login.captchaErrorDot'));
      return;
    }
  
    setIsLoading(true);

    const userData = {
      name: name,
      email: email,
      password: password,
      login_type: 'EMAIL',
    };

    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}users?create_account`, userData);
      //navigate('/login-check-valid', { state: { email: email, pre_access_token: response.data.data.pre_access_token } });
      navigate(`/login-check-valid?email=${encodeURIComponent(email)}&pre_access_token=${encodeURIComponent(response.data.data.pre_access_token)}`);
    } catch (error) {
      handleError(error, showSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const validatePasswordRepeat = () => {
    if (password !== passwordRepeat) {
      let error = {
        ['passwordRepeat']: t('login.passwordsDoNotMatch')
      };
      setErrors(error);
      refs.passwordRepeat.current.focus();
      return false;
    }

    setErrors({});
    return true;
  };

  return (
    <Container maxWidth="sm">
      <Box className="login-container">
        <Box 
          className="login-form" 
          padding={3} 
          boxShadow={3}
          component="form"
          onSubmit={handleSubmit}>
          <TextField
            label={t('login.nameLabel')}
            variant="outlined"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            inputRef={refs.name}
          />
          <TextField
            label={t('common.email')}
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            inputRef={refs.email}
          />
          <TextField
            label={t('common.password')}
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            inputRef={refs.password}
          />
          <TextField
            label={t('login.repeatPassword')}
            type="password"
            margin="normal"
            fullWidth
            value={passwordRepeat}
            onChange={(e) => setPasswordRepeat(e.target.value)}
            error={!!errors.passwordRepeat}
            helperText={errors.passwordRepeat}
            inputRef={refs.passwordRepeat}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />

          <Box display="flex" justifyContent="center" mt={2}>
            <ReCAPTCHA 
              ref={recaptcha} 
              sitekey={import.meta.env.VITE_RECAPCHA_SITE_KEY} 
            />
          </Box>

          <Box mt={4}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              onClick={handleSubmit}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? <CircularProgress size={24} /> : t('common.continue')}
            </Button>
          </Box>

        </Box>
      </Box>
    </Container>
  );
};

export default LoginCreateAccount;
